import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { useTranslation } from 'react-i18next';
import { convertToHTML } from 'draft-convert';

import styles from './WysiwygTextarea.module.scss';
import FlashMessage from '../FlashMessage/FlashMessage';
import cn from '../../../utils/tools/cn';

function WysiwygTextarea({
  name, onChange, rawValue, placeholder, error, formik, maxLength,
}) {
  const { t } = useTranslation();

  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [maxLengthError, setMaxLengthError] = useState(null);

  useEffect(() => {
    if (rawValue) {
      // populate state from html
      const blocksFromHTML = convertFromHTML(rawValue);
      if (blocksFromHTML) {
        const state = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap,
        );
        if (state) setEditorState(EditorState.createWithContent(state));
      }
    }
  }, [rawValue]);

  const getLink = (entity, text) => {
    if (entity && text) {
      return (
        <a
          href={entity.data.url}
          target={entity.data.targetOption || '_self'}
        >
          {text}
        </a>
      );
    }
    return text;
  };

  useEffect(() => {
    if (editorState) {
      const html = convertToHTML({
        entityToHTML: (entity, text) => {
          if (entity.type === 'LINK') return getLink(entity, text);
          return text;
        },
      })(editorState.getCurrentContent());

      onChange(html || '');
    }
  }, [editorState]);

  return (
    <div className={styles.max_wrapper}>
      <Editor
        editorState={editorState}
        wrapperClassName={cn([styles.textarea, error ? styles.error : ''])}
        localization={{ locale: 'fr' }}
        onEditorStateChange={(state) => {
          if (editorState !== state) setMaxLengthError(null);
          setEditorState(state);
        }}
        toolbar={{
          options: ['inline', 'list'],
          inline: {
            options: ['bold', 'italic', 'underline'],
          },
          list: {
            unordered: { className: 'rdw-ordered-list' },
            ordered: { className: 'd-none' },
            indent: { className: 'd-none' },
            outdent: { className: 'd-none' },
          },
        }}
        stripPastedStyles
        placeholder={placeholder}
        onBlur={() => formik?.setFieldTouched(name)}
        handleBeforeInput={(val) => {
          const textLength = editorState.getCurrentContent().getPlainText().length;

          if (val && textLength >= maxLength) {
            setMaxLengthError(t('camps.translations.maxLength', { maxLength }));
            return 'handled';
          }
          return 'not-handled';
        }}
        handlePastedText={(val) => {
          const textLength = editorState.getCurrentContent().getPlainText().length;
          if ((val.length + textLength) > maxLength) {
            setMaxLengthError(t('camps.translations.maxLength', { maxLength }));
          }
          return ((val.length + textLength) >= maxLength);
        }}
      />

      {error ? (
        <div id={`error_${name}`} className="m-t-10">
          <FlashMessage message={t(error)} color="error" />
        </div>
      ) : null}

      {maxLengthError ? (
        <div className="m-t-10">
          <FlashMessage message={maxLengthError} color="error" />
        </div>
      ) : null}
    </div>
  );
}

WysiwygTextarea.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  rawValue: PropTypes.node,
  error: PropTypes.string,
  formik: PropTypes.shape({
    setFieldTouched: PropTypes.func,
  }),
  maxLength: PropTypes.number,
};

WysiwygTextarea.defaultProps = {
  name: '',
  onChange: () => {},
  placeholder: null,
  rawValue: null,
  error: null,
  formik: null,
  maxLength: 2950,
};

export default WysiwygTextarea;
